"use client"

import Image from "next/image"
import { useTheme } from "@/components/theme-provider"

interface A9entLogoProps {
  /** Height of the logo in pixels */
  height?: number
  /** Width of the logo - auto if not set (maintains aspect ratio) */
  width?: number
  /** Force dark background variant (always use logo-light) - e.g. for fixed dark sections */
  darkBackground?: boolean
  /** Force light background variant (always use logo-dark) */
  lightBackground?: boolean
  /** Additional class names */
  className?: string
}

/**
 * A9ent logo that switches between logo-light and logo-dark based on theme.
 * - Dark mode / dark background → logo-light (light-colored logo)
 * - Light mode / light background → logo-dark (dark-colored logo)
 */
export function A9entLogo({
  height = 32,
  width,
  darkBackground = false,
  lightBackground = false,
  className,
}: A9entLogoProps) {
  const { resolvedTheme } = useTheme()

  // logo-light = for dark backgrounds, logo-dark = for light backgrounds
  const useLightLogo =
    darkBackground || (resolvedTheme === "dark" && !lightBackground)
  const logoSrc = useLightLogo ? "/logo-dark.png" : "/logo-light.png"

  const imgWidth = width ?? Math.round(height * 2.5)

  return (
    <Image
      src={logoSrc}
      alt="A9ent"
      height={height}
      width={imgWidth}
      className={className}
      priority
    />
  )
}
